// Build copilot: derives the AI's "API" (node catalog + tool schemas + system
// prompt) from the existing VERBS catalog, so the assistant edits the real flow
// using the same vocabulary the canvas understands. The agentic loop lives in
// CopilotPanel; the tools are executed by the Builder's canvas mutators.
import { VERBS } from "./verbs";
import { triggerList } from "./triggers";

// catálogo DETALHADO derivado do VERBS (fonte única — nunca um doc paralelo):
// por nó, todos os params (com required/options/default/label), as saídas com
// rótulo, as flags e uma dica opcional `ai`. É o que o modelo precisa para
// montar sem alucinar campos/en;valores.
// TODOS os verbos entram no catálogo, inclusive as AÇÕES de conector (conn.<tipo>.*)
// de conectores AINDA NÃO configurados — estes vêm marcados com needsSetup:true (o
// nó mostra "(Configurar)" no canvas). ctx.connectors só define o que é needsSetup.
export const buildCatalog = (ctx, opts) => {
  const connTypes = new Set(((ctx && ctx.connectors) || []).map(c => c.type));
  return Object.keys(VERBS)
    // inclui TODOS os verbos (inclusive conectores ainda NÃO configurados), MENOS
    // os terminais de OUTRO modo: `return` (só Ferramenta) e `collector_return`
    // (só Coletor) — o copiloto de automação não deve adicioná-los.
    .filter(k => !k.startsWith("__") && !VERBS[k].toolOnly && !VERBS[k].collectorOnly)
    .map(k => {
      const v = VERBS[k];
      const defaults = v.defaults || {};
      // conector ainda não configurado: usável mesmo assim (o nó mostra "(Configurar)")
      const ct = k.startsWith("conn.") ? k.split(".")[1] : null;
      const needsSetup = !!ct && !connTypes.has(ct);
      const entry = {
        verb: k,
        title: v.title,
        category: v.category,
        params: (v.fields || []).map(f => {
          const p = { name: f.name, type: f.type };
          if (f.required) p.required = true;
          if (f.options) p.options = f.options; // valores válidos (enum)
          if (f.label) p.label = f.label; // descrição curta do campo
          if (defaults[f.name] !== undefined && defaults[f.name] !== "") p.default = defaults[f.name];
          return p;
        }),
        outputs: (v.handles || []).map(h => (h.label ? { id: h.id, label: h.label } : { id: h.id })),
      };
      // dados que o nó PRODUZ (referencie depois como steps.<step_do_nó>.<chave>,
      // onde <step> é o campo `step` devolvido por read_flow/add_node — NUNCA o id),
      // para o modelo encadear campos entre nós sem inventar caminhos
      if (Array.isArray(v.exports) && v.exports.length) {
        entry.produces = v.exports.map(e => (e.label ? { key: e.key, label: e.label } : { key: e.key }));
      }
      if (v.isBranch) {
        entry.branch = true; // tem múltiplas saídas condicionais
        // `caseKeys` NÃO é um field (não aparece no editor como campo comum), então
        // não entrava em `params` e o modelo nem sabia que existia — o nó nascia
        // sempre com o default ["1","2"], mesmo quando a variável era texto.
        // sem `default` de propósito: expor ["1","2"] ancorava o modelo no valor
        // errado — era exatamente o sintoma (todo Condicional saía com 1 e 2).
        entry.params.push({
          name: "caseKeys",
          type: "string[]",
          required: true,
          label: "Valores que a variável pode ter — UM caso por valor. Texto ou número.",
        });
      }
      if (v.isIvr) entry.ivr = true; // menu de URA (uma saída por opção)
      // Menu WhatsApp: as SAÍDAS são as próprias opções (o texto de cada uma) +
      // "default" (sem opção). Conecte cada uma via connect (source_handle = o texto).
      if (v.isWaMenu) entry.waMenu = true;
      if (v.ai) entry.ai = v.ai; // dica de "quando usar" (opcional, no VERBS)
      // needsSetup (conector sem instância) só quando pedido: o catálogo do copiloto
      // de automação vai SEM ele para ser idêntico em todas as contas (cache entre
      // conversas — STU-160); a lista do que falta configurar vai no contexto da conta
      if (needsSetup && !(opts && opts.static)) entry.needsSetup = true;
      return entry;
    });
};

export const TRIGGER_TYPES = [
  { type: "inbound_call", label: "Ao receber chamada (entrante)" },
  { type: "api_call", label: "Ao atender chamada iniciada pela API" },
  { type: "webhook", label: "Ao receber um webhook/HTTP" },
  { type: "scheduled", label: "Agendamento (horário)" },
  // consumidor de Coletor de dados: roda 1x por registro novo (config: collectorId).
  // O registro chega em steps.trigger.event.<coluna> (SEM .body).
  { type: "data_collector", label: "Coletor de dados (1x por registro novo; config collectorId)" },
];

// Anthropic-style tool schemas (the backend translates to OpenAI when needed).
export const TOOLS = [
  {
    name: "read_flow",
    description:
      "Lê o estado atual do fluxo: `app` (id, name, published, base_url e `endpoint` — COMO chamar este app: URL do webhook, POST /v1/calls para api_call, número para inbound_call, cron para agendado), o `trigger`/`trigger_config`, os `nodes` (id, verbo, params, produces) e as `edges`. Use antes de editar um fluxo existente E sempre que o usuário perguntar como chamar/testar o app (ex.: um curl) — pegue a URL real de app.endpoint em vez de inventar. Segredos (secret do webhook, API key) NÃO vêm aqui; apenas indique onde o usuário os encontra.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "set_trigger",
    description:
      "Define o gatilho do fluxo (o ponto de partida, nó de id 'start'). Necessário antes de publicar. Passe também `config` com os campos do gatilho quando o usuário os informar. Para gatilhos webhook/evento, passe TAMBÉM `sample`: um exemplo do corpo (JSON) que vai chegar, com os campos que o fluxo usa — vira o valor inicial do botão 'Enviar teste' e ajuda o mapeamento de variáveis (steps.trigger.event.body.*).",
    input_schema: {
      type: "object",
      properties: {
        trigger_type: { type: "string", description: "Um dos trigger_type listados em 'Gatilhos disponíveis' no contexto. Inclui os estáticos (inbound_call, api_call, webhook, scheduled) e os de conector no formato event.<conector>.<evento> (ex.: event.movidesk.ticket_created)." },
        config: {
          type: "object",
          description:
            "Configuração do gatilho (campos específicos do tipo). Para inbound_call (Ao receber chamada), use { inboundNumberId: <ID do número da conta> } — o ID vem da lista 'Números da conta (id:número)' do contexto; NÃO use o número em texto, use o id. Para gatilho de evento de CONECTOR (event.<conector>.*, ex.: event.gupshup.ao_receber_mensagem), use { connectorId: <ID do conector daquele tipo> } — a conta pode ter VÁRIOS; pegue o id da lista 'Conectores (id:nome:tipo)' do contexto (o de type igual ao do gatilho); se houver mais de uma conexão do MESMO CANAL — inclusive de tipos diferentes, ex.: WhatsApp QRCode (whatsapp_qr) + API Oficial (gupshup) — pergunte qual via ask_user ANTES de chamar set_trigger. Para SCHEDULED (Agendado), use { cron: '<m h dom mon dow>', crons: ['<m h dom mon dow>'] } — um CRON de 5 campos no horário pedido (NÃO deixe o padrão). Ex.: todo dia às 14h = '0 14 * * *'; seg–sex às 9h = '0 9 * * 1,2,3,4,5'; dia 1 de cada mês às 8h = '0 8 1 * *'; vários horários = vários itens em crons. Para WEBHOOK: { method, responseStatus, secret }. O responseStatus decide o que quem CHAMOU o webhook recebe de volta: um código fixo ('200', '202', …) responde na hora, ignorando o resultado do fluxo; 'flow' (Responder no fluxo) faz a requisição ESPERAR o fluxo terminar e devolver o que o nó 'http_response' montar. Use 'flow' SEMPRE que o chamador precisar de uma RESPOSTA COM DADOS (consulta, cálculo, status, id criado) — e, nesse caso, o fluxo é OBRIGADO a ter um nó http_response no fim de cada caminho, senão o chamador recebe um retorno vazio. Para webhook que só dispara algo em segundo plano (notificação, gravar log), use um código fixo — é mais rápido e não segura o chamador. Omita se não souber.",
        },
        sample: {
          type: "object",
          description:
            "Exemplo do corpo (JSON) que o webhook/evento recebe — só os campos que o fluxo realmente usa, com valores plausíveis. Ex. p/ um endpoint de OTP: { \"userId\": \"12345\", \"email\": \"cliente@exemplo.com\" }. Preenche o teste do gatilho e o picker de variáveis.",
        },
      },
      required: ["trigger_type"],
    },
  },
  {
    name: "add_node",
    description:
      "Cria um nó e o conecta após after_id (use 'start' para o primeiro nó depois do gatilho). Encadeie passando o id retornado como after_id do próximo — OU, para montar VÁRIOS nós na MESMA resposta, dê um apelido em `ref` e use 'ref:<apelido>' como after_id/source_id/target_id/node_id dos seguintes. Retorna { id, ref }.",
    input_schema: {
      type: "object",
      properties: {
        verb: { type: "string", description: "chave do verbo do catálogo (ex.: say, gather, dial, hangup)" },
        params: { type: "object", description: "parâmetros do nó conforme os 'params' do catálogo" },
        after_id: { type: "string", description: "id do nó (ou 'start') ao qual conectar a saída" },
        after_handle: { type: "string", description: "saída do nó origem (padrão 'next')" },
        ref: { type: "string", description: "apelido curto deste nó (ex.: 'email', 'menu') para os outros nós desta resposta o referenciarem como 'ref:email'." },
      },
      required: ["verb"],
    },
  },
  {
    name: "connect",
    description: "Conecta a saída de um nó (source_handle, padrão 'next') a outro nó. source_id/target_id aceitam 'ref:<apelido>' de um add_node.",
    input_schema: {
      type: "object",
      properties: {
        source_id: { type: "string" },
        target_id: { type: "string" },
        source_handle: { type: "string" },
      },
      required: ["source_id", "target_id"],
    },
  },
  {
    name: "update_node",
    description: "Atualiza os params de um nó existente (merge).",
    input_schema: {
      type: "object",
      properties: { node_id: { type: "string" }, params: { type: "object" } },
      required: ["node_id", "params"],
    },
  },
  {
    name: "delete_node",
    description: "Remove um nó e suas conexões.",
    input_schema: { type: "object", properties: { node_id: { type: "string" } }, required: ["node_id"] },
  },
  {
    name: "validate_flow",
    description:
      "Valida o fluxo atual com as MESMAS regras da publicação (campos obrigatórios, gatilho, loops balanceados, branch/URA com destino, etc.). Retorna { ok, issues:[{message, nodeId}] }. Chame SEMPRE ao terminar de montar/editar e corrija os issues antes de concluir.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "set_name",
    description: "Define o nome do fluxo (necessário antes de publicar).",
    input_schema: { type: "object", properties: { name: { type: "string" } }, required: ["name"] },
  },
  {
    name: "publish_flow",
    description:
      "Publica o fluxo (coloca no ar). Valida antes; se houver problemas retorna { ok:false, issues } e se faltar nome { ok:false, needsName:true } — resolva e tente de novo. Sucesso: { ok:true, version }.",
    input_schema: { type: "object", properties: {} },
  },
  {
    name: "place_call",
    description:
      "Disca AGORA: origina uma chamada para 'to' que, ao atender, roda ESTE fluxo. Exige o gatilho 'api_call' (Ao atender chamada) e o fluxo PUBLICADO. Use para testar/executar a ligação. Retorna { ok, sid } ou { ok:false, error }.",
    input_schema: {
      type: "object",
      properties: { to: { type: "string", description: "número de destino" }, from: { type: "string", description: "Caller ID (número da conta, opcional)" } },
      required: ["to"],
    },
  },
  {
    name: "set_notes",
    description:
      "Escreve as NOTAS do fluxo (o 'readme' que quem abrir depois vai ler). Use ao terminar de montar ou alterar um fluxo, SEM o usuário pedir: a documentação nasce junto com o fluxo em vez de virar um campo vazio. Markdown curto, em português, na estrutura: '## O que faz', '## Gatilho', '## Passos', '## Atenção'. Em Atenção use citação ('> ') para o que quebra na hora de testar — por exemplo, que o gatilho real é inbound_call e para testar é preciso trocar para api_call e depois devolver. Descreva o COMPORTAMENTO em linguagem de produto; não cite nomes internos de verbos. Se já houver notas, reescreva mantendo o que o usuário tiver acrescentado à mão.",
    input_schema: {
      type: "object",
      properties: {
        notes: { type: "string", description: "O readme do fluxo em markdown." },
      },
      required: ["notes"],
    },
  },
  {
    name: "ask_user",
    description:
      "Faça UMA pergunta ao usuário quando precisar de uma informação ESSENCIAL que não dá para assumir. Regras: UMA pergunta por chamada (nunca várias de uma vez); prefira opções (choice/multi) quando houver alternativas claras; só use 'text' para resposta realmente livre. O resultado da tool É a resposta do usuário — continue o trabalho com ela. NÃO faça perguntas que dá para deduzir do contexto da conta ou do catálogo.",
    input_schema: {
      type: "object",
      properties: {
        question: { type: "string", description: "A pergunta, curta e direta (1 frase)." },
        input_type: {
          type: "string",
          enum: ["text", "choice", "multi"],
          description: "text = resposta livre; choice = escolher UMA opção; multi = escolher VÁRIAS.",
        },
        options: {
          type: "array",
          items: { type: "string" },
          description: "Opções para choice/multi (2 a 6 itens curtos). Omita em 'text'.",
        },
        placeholder: { type: "string", description: "Dica do campo, só para input_type='text' (opcional)." },
      },
      required: ["question", "input_type"],
    },
  },
  {
    name: "create_connector",
    description:
      "Abre um formulário para o USUÁRIO configurar (e criar) um conector AQUI MESMO, sem mandá-lo para outra tela. Funciona para QUALQUER tipo de conector: mysql, smtp (e-mail próprio), movidesk, rdstation, bling, google, etc. Use SEMPRE que o usuário quer usar/conectar algo que precisa de credenciais e ainda NÃO está configurado na conta (ex.: 'conectar no meu MySQL', 'usar meu e-mail'), em vez de mandá-lo configurar em Conectores. As credenciais são digitadas pelo usuário na UI e NUNCA passam por você. Retorna { connector_id, verbs: [{key,title}] } — para conectores de AÇÃO NATIVA use o connector_id em params.connectorId do add_node e os 'verbs' como o verbo do nó; para mysql/smtp use o connector_id no campo de conexão do nó correspondente (verbs vem vazio, tudo bem). Se o usuário cancelar, retorna { canceled: true }.",
    input_schema: {
      type: "object",
      properties: {
        type: { type: "string", description: "tipo do conector: mysql, smtp, ou um dos tipos da lista de integrações do contexto (ex.: movidesk)." },
      },
      required: ["type"],
    },
  },
  {
    name: "propose_whatsapp_template",
    description:
      "Propõe um template de WhatsApp (API OFICIAL/Gupshup) para um nó 'Enviar template' (conn.gupshup.enviar_template) que você JÁ criou. NÃO submete sozinho: cria um RASCUNHO que vira uma pendência no canvas para o usuário CONFIRMAR e então o sistema submete ao WhatsApp (aprovação é assíncrona). Use quando o fluxo precisa enviar um template que ainda não existe. REGRAS do conteúdo (a Meta rejeita se quebrar): não COMECE nem TERMINE o texto com variável; use {{1}},{{2}},{{3}}… em ordem, sem pular números (ex.: 'Olá {{1}}, sua consulta é {{2}}.').",
    input_schema: {
      type: "object",
      properties: {
        node_id: { type: "string", description: "id do nó 'Enviar template' que vai usar este template (crie o nó antes com add_node verb=conn.gupshup.enviar_template)." },
        element_name: { type: "string", description: "nome técnico do template, snake_case, sem espaços/acentos. Ex.: clinica_lembrete_consulta" },
        category: { type: "string", enum: ["UTILITY", "MARKETING", "AUTHENTICATION"], description: "UTILITY = transacional (lembrete/confirmação); MARKETING = promocional; AUTHENTICATION = código." },
        language_code: { type: "string", description: "idioma do template (padrão pt_BR). Ex.: pt_BR, en_US, es." },
        content: { type: "string", description: "corpo do template com {{1}},{{2}}… em ordem; não comece/termine com variável." },
        examples: {
          type: "array",
          items: { type: "string" },
          description: "Valores de AMOSTRA para as variáveis {{1}},{{2}}… do CORPO, NA ORDEM (ex.: ['Maria','25/06 às 14h']). A Meta REVISA por estes valores — use exemplos realistas (não 'exemplo'); isso acelera a aprovação. Quantos itens = quantas variáveis no content.",
        },
        header: { type: "string", description: "cabeçalho (opcional). Pode conter UMA variável {{1}} (ex.: 'Pedido {{1}}')." },
        header_example: { type: "string", description: "valor de amostra para a variável do cabeçalho, se o header tiver {{1}} (ex.: '2026000123')." },
        footer: { type: "string", description: "rodapé (opcional, sem variáveis)." },
        buttons: {
          type: "array",
          description: "Botões do template (opcional). MENU/URA → use QUICK_REPLY, UM por opção (ex.: 'Vendas','Suporte'; até 10). Ações (call-to-action) → URL (abre um link, ex.: site/agendamento) e/ou PHONE_NUMBER (liga); até 2 de ação. NÃO misture QUICK_REPLY com URL/telefone no mesmo template.",
          items: {
            type: "object",
            properties: {
              type: { type: "string", enum: ["QUICK_REPLY", "URL", "PHONE_NUMBER"], description: "QUICK_REPLY = resposta rápida (menu); URL = abre link; PHONE_NUMBER = liga." },
              text: { type: "string", description: "rótulo do botão (curto, ~20 chars; ex.: 'Agendar', 'Falar agora')." },
              url: { type: "string", description: "obrigatório para type=URL — link COMPLETO e estático (ex.: https://clinica.com/agendar)." },
              phone_number: { type: "string", description: "obrigatório para type=PHONE_NUMBER — com DDI (ex.: +5511999990000)." },
            },
            required: ["type", "text"],
          },
        },
      },
      required: ["node_id", "element_name", "category", "content"],
    },
  },
  {
    name: "read_recent_executions",
    description:
      "Lê as EXECUÇÕES recentes (o histórico de runs) para DIAGNOSTICAR o que aconteceu — principalmente FALHAS. Use SEMPRE que o usuário perguntar sobre a última execução, por que algo falhou, o que deu errado, ou pedir para investigar/resolver um problema no fluxo: NÃO peça para ele colar logs, busque aqui. Sem runId: retorna as execuções mais recentes primeiro (padrão as 10 últimas do fluxo atual), cada uma com { runId, startedAt, endedAt, status ('error' | 'success' | 'info'), count, label, trigger }. Para entender POR QUE uma execução se comportou assim, chame DE NOVO passando o runId dela: retorna a cadeia de passos, e CADA passo traz os VALORES BRUTOS { type, status, message, node, step, input, output, at } — ou seja, você VÊ o conteúdo real (o body que chegou no gatilho, os parâmetros e a saída de cada nó), não só quais passos rodaram. Use input/output para comparar o que foi recebido vs. o que era esperado (ex.: o code que chegou vs. o code salvo). O passo com status 'error' traz o motivo em `message`. (Valores muito grandes vêm truncados como { _truncado, _amostra }.) NUNCA crie um nó de debug/temporário no fluxo para inspecionar valores — os valores estão AQUI, é só passar o runId; criar nó para 'ver' o que chegou é ERRADO. Passe all_flows=true para ver execuções de TODA a conta (não só do fluxo aberto). É só LEITURA — não altera nada.",
    input_schema: {
      type: "object",
      properties: {
        limit: { type: "number", description: "Quantas execuções recentes listar (padrão 10, máx 20). Ignorado quando runId é passado." },
        runId: { type: "string", description: "Se informado, retorna a cadeia de passos/eventos DAQUELA execução (com a mensagem de erro do passo que falhou), em vez da lista." },
        all_flows: { type: "boolean", description: "true = execuções de TODA a conta; padrão false = só o fluxo atualmente aberto." },
      },
    },
  },
];

// bloco de recursos REAIS da conta (ids/valores que o modelo deve referenciar
// em vez de inventar): números, conectores, agentes de IA e áudios.
export const buildContextPrompt = ctx => {
  if (!ctx) return "";
  const lines = [];
  if (ctx.numbers && ctx.numbers.length)
    lines.push(
      `Números da conta (id:número — para o gatilho inbound_call, use o ID em config.inboundNumberId): ${ctx.numbers
        .map(n => `${n.id}:${n.number}`)
        .join(", ")}`
    );
  if (ctx.connectors && ctx.connectors.length)
    lines.push(`Conectores (id:nome:tipo[:status]): ${ctx.connectors.map(c => `${c.id}:${c.name}:${c.type}${c.status ? `:${c.status}` : ""}`).join("; ")}`);

  // gatilhos disponíveis: os estáticos + os de EVENTO dos conectores que a conta tem
  // (event.<conector>.<evento>, ex. movidesk). Sem isso o modelo não conhece esses.
  const connTypes = new Set((ctx.connectors || []).map(c => c.type));
  const staticT = TRIGGER_TYPES.map(t => `${t.type} (${t.label})`);
  const eventTriggers = triggerList().filter(
    t => t.key && String(t.key).startsWith("event.") && connTypes.has(t.connector)
  );
  const eventT = eventTriggers.map(t => `${t.key} (${t.title})`);
  const allT = [...staticT, ...eventT];
  if (allT.length) lines.push(`Gatilhos disponíveis (trigger_type): ${allT.join("; ")}`);

  // CONFIG de cada gatilho estático, derivada do triggerList (fonte única). Antes
  // o modelo só recebia "tipo (título)" e tinha de adivinhar o `config` — por isso
  // não sabia que responseStatus aceita "flow" (responder no fluxo), que nem
  // aparecia em lugar nenhum do prompt.
  const optLabel = o => (typeof o === "object" ? o.value : o);
  const trigCfg = triggerList()
    .filter(t => TRIGGER_TYPES.some(s => s.type === t.key) && (t.fields || []).length)
    .map(t => {
      const fs = t.fields
        .map(f => (f.options ? `${f.name}=${f.options.map(optLabel).join("|")}` : f.name))
        .join(", ");
      return `${t.key}: ${fs}`;
    });
  if (trigCfg.length) lines.push(`Config de cada gatilho (campos de set_trigger.config): ${trigCfg.join(" · ")}`);

  // integrações com AÇÕES NATIVAS (conn.*) que a conta ainda NÃO configurou — pra o
  // copiloto GUIAR o usuário a configurar o conector em vez de cair em HTTP genérico.
  const capTypes = Array.from(
    new Set(Object.keys(VERBS).filter(k => k.startsWith("conn.")).map(k => k.split(".")[1]))
  );
  const availableConn = capTypes.filter(t => !connTypes.has(t));
  if (availableConn.length)
    lines.push(
      `Integrações com AÇÕES NATIVAS ainda NÃO configuradas na conta: ${availableConn.join(", ")}. ` +
        `IMPORTANTE: os verbos nativos delas (conn.<tipo>.*) ESTÃO no catálogo e você DEVE ` +
        `USÁ-LOS mesmo sem o conector configurado — deixe o connectorId vazio. O nó aparece com a tag "(Configurar)" ` +
        `no canvas e o usuário configura o conector depois para ativar. NÃO use http_request quando existe verbo ` +
        `nativo. Opcional: se o usuário QUISER configurar agora, chame create_connector(type).`
    );

  // schema de SAÍDA dos gatilhos de evento (quando conhecido): os campos que
  // chegam no steps.trigger.event.body. Assim o modelo mapeia variáveis SEM o
  // usuário ter que explicar o formato do JSON do conector (ex.: Movidesk).
  eventTriggers
    .filter(t => t.out && t.out.fields)
    .forEach(t => {
      const fl = t.out.fields.map(f => `steps.trigger.event.body.${f.path} — ${f.desc}`).join("; ");
      lines.push(`Campos de ${t.key} (use estes caminhos como variáveis): ${fl}`);
    });
  if (ctx.agents && ctx.agents.length)
    lines.push(`Agentes de IA (id:nome): ${ctx.agents.map(a => `${a.id}:${a.name}`).join("; ")}`);
  // tabelas da conta: podem ser FONTE de consulta (record_query), não só registro
  if (ctx.datasets && ctx.datasets.length)
    lines.push(
      `Tabelas da conta (id:"nome" — campos; leia com record_query, grave com record_save): ${ctx.datasets
        .map(d => `${d.id}:"${d.name}" — ${(d.fields || []).map(f => f.name).join(", ") || "sem campos"}`)
        .join("; ")}`
    );
  if (ctx.audios && ctx.audios.length)
    lines.push(`Áudios (id:nome): ${ctx.audios.map(a => `${a.id}:${a.name}`).join("; ")}`);
  if (!lines.length) return "";
  return ["", "", "Recursos REAIS da conta — use estes ids/valores, NÃO invente:", ...lines].join("\n");
};

export const buildSystemPrompt = (catalog, context = "", opts = {}) => {
  const lines = [
    "Você é o copiloto do Numera Studio — uma plataforma de telefonia/CPaaS. Você ajuda o",
    "usuário a CONSTRUIR e EDITAR o fluxo do app dele, conversando em português de forma",
    "natural, amigável e concisa, como um colega que entende de telefonia.",
    "",
    "ESTILO DA RESPOSTA (não poluir o chat):",
    "- Responda em UMA mensagem. NÃO narre o processo ('vou abrir…', 'deixa comigo', 'encontrei!', 'já já', 'só um instante'): faça o que precisa e entregue a resposta direto.",
    "- SEM entusiasmo vazio nem bajulação ('beleza!', 'perfeito!', 'ótima pergunta!'). Tom de colega, direto e curto.",
    "- Formatação enxuta: no máximo UM bloco de código quando fizer diferença; evite encher de negrito, títulos e pílulas de código inline.",
    "",
    "LER ≠ EDITAR (CRÍTICO): para PERGUNTAS de leitura/diagnóstico (o que aconteceu, por que falhou, quais valores chegaram, ver a última execução, como está o fluxo), use SOMENTE ferramentas de leitura (read_flow, read_recent_executions, validate_flow). NUNCA edite o fluxo (add_node/update_node/delete_node/connect/set_trigger) para RESPONDER uma pergunta — só edite quando o usuário PEDIR explicitamente uma mudança no fluxo.",
    "",
    "COMO SE COMPORTAR (importante) — seja o MAIS AUTÔNOMO possível:",
    "- Converse de verdade. Se a pessoa só cumprimentar ou perguntar algo, responda normalmente — não saia construindo.",
    "- Para pedidos de criar/editar fluxo, a REGRA É: MONTE PRIMEIRO, PERGUNTE DEPOIS. Construa já uma",
    "  primeira versão COMPLETA do fluxo usando padrões razoáveis e PLACEHOLDERS, e só ENTÃO pergunte os",
    "  detalhes que faltam — e apenas o ESSENCIAL.",
    "- NÃO faça questionário antes de montar. Ex.: num nó de fala, monte com um texto placeholder",
    "  (ex.: 'Olá! [mensagem a definir]') e DEPOIS pergunte qual texto a pessoa quer. Mesma ideia para",
    "  áudio, destino de transferência, opções da URA, mensagem do WhatsApp, etc.",
    "- Use os recursos REAIS da conta (números/ramais/conectores/agentes/áudios) quando existirem. Se um",
    "  valor essencial não tiver padrão (ex.: número de destino), use um placeholder e ANOTE para perguntar.",
    "- Depois de montar e validar (validate_flow), explique em 1–2 frases o que montou e ENTÃO faça as",
    "  perguntas pendentes — mas SEMPRE pela tool ask_user, UMA pergunta POR VEZ (nunca despeje várias num",
    "  texto). Espere a resposta de cada uma antes da próxima. Use opções (choice/multi) quando houver",
    "  alternativas claras; 'text' só para resposta livre. Não pergunte o que dá para assumir.",
    "- NUNCA escreva perguntas soltas no texto (ex.: 'Qual o número de destino?'). Pergunta = ask_user.",
    "- Só pergunte ANTES de montar se for IMPOSSÍVEL montar sem aquilo (raro) — e ainda assim via ask_user.",
    "- Você pode alternar entre conversar, montar e ajustar ao longo do diálogo.",
    "",
    "QUANDO SUGERIR UM APP NOVO (em vez de inchar o app atual):",
    "- Cada app tem UM gatilho e UM objetivo. Se o novo pedido tem PROPÓSITO diferente do app atual, ou exige",
    "  um GATILHO diferente do que já está definido, é caso de APP NOVO — um app não mistura gatilhos/objetivos.",
    "- Considere app novo também se atender o pedido deixaria ESTE fluxo complexo demais: muitas condicionais,",
    "  ramificações demais, ou passando de ~5 nós. Fluxos enxutos são mais fáceis de entender, manter e testar.",
    "- Nesses casos NÃO saia construindo no app atual: PERGUNTE via ask_user se a pessoa prefere que você gere",
    "  um TEXTO DE HANDOFF para ela colar num chat NOVO (que vira o outro app). Opções (choice): 'Gerar handoff'",
    "  e 'Continuar aqui mesmo'.",
    "- Se ela aceitar, escreva o handoff como uma mensagem sua (um bloco que ela copia): resumo claro e",
    "  AUTOSSUFICIENTE do novo app — objetivo, gatilho sugerido, passo a passo do que deve fazer, dados/variáveis",
    "  e integrações envolvidas — pro copiloto do outro chat montar do zero, sem depender deste contexto/histórico.",
    "- Se ela preferir seguir aqui, continue no app atual normalmente. (Em MODO DEMONSTRAÇÃO você NÃO pergunta:",
    "  apenas monte no app atual da melhor forma.)",
    "",
    "COMO CONSTRUIR (ferramentas):",
    "- O fluxo é um grafo. O gatilho é o nó fixo de id 'start' (ponto de entrada).",
    "- Cada nó tem um 'verbo' (do catálogo) e parâmetros, conectado por saídas (handles).",
    "- Primeiro nó depois do gatilho: add_node com after_id='start'. Para encadear, use o id retornado",
    "  como after_id do próximo. Use connect para ligações extras (saídas de branch/URA).",
    "- Use EXCLUSIVAMENTE as chaves de verbo e nomes de params do catálogo abaixo. NÃO invente verbos/params.",
    "- Respeite os campos: 'required' (obrigatório), 'options' (use SÓ um desses valores), 'default' (valor padrão),",
    "  'outputs' (saídas — em branch/URA conecte CADA saída ao destino certo via connect).",
    "- Campos que referenciam recursos (voz, áudio, conector, agente) usam ids/valores reais do contexto da conta.",
    "",
    "VARIÁVEIS ENTRE NÓS (CRÍTICO — não inventar caminhos):",
    "- Dados do GATILHO: use SOMENTE os caminhos listados em 'Campos de <gatilho>' no contexto",
    "  (ex.: {{steps.trigger.event.body.from}}). NUNCA invente paths (ex.: event.sender, event.telefone).",
    "  Se o gatilho não listar um campo, ele não existe — não chute.",
    "- Saída de um NÓ anterior: referencie como {{steps.<step>.<chave>}}, onde <step> é o campo 'step' que",
    "  read_flow/add_node devolvem para cada nó (é o NOME da variável, derivado do verbo: 'gather', 'code',",
    "  'code_2'…). NUNCA use o id do nó (n_3, _4): 'steps.n_3'/'steps._4' NÃO existem em runtime. <chave> é",
    "  uma das listadas em 'produces' (ex.: {{steps.http_request.json.algumCampo}} para um http_request).",
    "- EXCEÇÃO — nó 'Executar código' (code): o campo `code` é JavaScript PURO; {{variáveis}} NÃO",
    "  são substituídas dentro dele (chegam como texto → bug). Leia valores como objeto JS:",
    "  steps.<step>.<chave> e inputs.session.<campo> (contexto do nó 'Definir contexto'). Ex.:",
    "  `const tentativas = parseInt(inputs.session.attempts) || 0;` — NUNCA parseInt('{{...}}').",
    "- Responder a uma mensagem recebida (WhatsApp/chat): o destino é o REMETENTE — use o campo 'from' do gatilho",
    "  ({{steps.trigger.event.body.from}}), nunca um número fixo nem um campo inventado.",
    "- Na dúvida sobre um campo, chame read_flow / olhe o schema do gatilho ANTES de escrever a variável.",
    "- Edite fluxo existente? Chame read_flow primeiro para entender o que já existe.",
    "- Defina o gatilho com set_trigger num fluxo novo que ainda não tem gatilho.",
    "- Se o usuário disser EM QUAL número o fluxo deve atender (ex.: 'ao receber chamada no NÚMERO X'),",
    "  passe config.inboundNumberId no set_trigger com o ID daquele número (mapeie o número falado → id pela",
    "  lista 'Números da conta (id:número)' do contexto). Se ele não disser o número, deixe sem e siga.",
    "- Gatilho de EVENTO de conector (event.<conector>.*, ex.: event.gupshup.ao_receber_mensagem): SEMPRE passe",
    "  config.connectorId com o id do conector daquele tipo (a conta pode ter vários). Pegue da lista 'Conectores",
    "  (id:nome:tipo)'. Se houver MAIS DE UM do mesmo tipo, pergunte qual via ask_user antes de definir o gatilho.",
    "- Gatilho SCHEDULED (Agendado): SEMPRE traduza o horário pedido para um CRON de 5 campos e passe em",
    "  config.cron E config.crons (ex.: 'todo dia às 14h' → cron '0 14 * * *'). NUNCA deixe o horário padrão (9h).",
    "- Nós de chamada (say, play, gather, dial, hangup, etc.) exigem um gatilho de chamada (inbound_call/api_call).",
    "- CONVERSAR AO TELEFONE (preferência): quando o fluxo precisa FALAR/dialogar com a pessoa na ligação (atender,",
    "  perguntar e entender respostas, tirar dúvidas, dar informação), PREFIRA entregar a um Agente de IA de voz —",
    "  verbo 'transfer_ai' ('Transferir para IA') — em vez de encadear 'say'/'gather'. O agente conversa em tempo",
    "  real e é muito mais natural; say/gather é roteiro rígido turno-a-turno. 'transfer_ai' exige um agentId: use",
    "  um agente de voz da conta (lista do contexto); se não houver nenhum ou o usuário não indicar qual, pergunte",
    "  via ask_user. EXCEÇÃO (aí sim use say/gather): o usuário pediu um SCRIPT FIXO / URA determinística ('toque 1",
    "  para vendas'), ou é só uma frase única sem conversa (um aviso curto e desligar).",
    "- WhatsApp tem DOIS caminhos distintos: 'brchat_send' (BR CHAT) usa a API NÃO OFICIAL do WhatsApp;",
    "  'conn.gupshup.enviar_whatsapp'/'conn.gupshup.enviar_template' (Gupshup) usam a API OFICIAL. Quando o",
    "  usuário NÃO especificar oficial vs não oficial, PREFIRA SEMPRE a API OFICIAL (Gupshup). Só use a não",
    "  oficial (BR CHAT) se o usuário pedir explicitamente ou se apenas ela estiver configurada na conta.",
    "- ENVIAR mensagem de WhatsApp (oficial/Gupshup) é SEMPRE por TEMPLATE: use 'conn.gupshup.enviar_template'",
    "  (NUNCA 'conn.gupshup.enviar_whatsapp' de texto livre). Regra do WhatsApp: mensagem que a EMPRESA inicia",
    "  (boas-vindas, aviso, cobrança, notificação, disparo por evento/cadastro etc.) EXIGE template aprovado —",
    "  texto livre só funciona respondendo dentro da janela de 24h (isso é o wa_menu, abaixo). Então, ao montar",
    "  um envio de WhatsApp, crie o nó conn.gupshup.enviar_template e CHAME propose_whatsapp_template (ver abaixo).",
    "- BOT/MENU de WhatsApp (conversa que RESPONDE a quem mandou mensagem — gatilho",
    "  event.gupshup.ao_receber_mensagem): use o nó 'wa_menu' (Menu WhatsApp), NÃO branch nem template.",
    "  O wa_menu envia a mensagem + opções (botões) e ESPERA a resposta; cada opção é uma SAÍDA. Monte:",
    "  add_node verb=wa_menu com params { connectorId (gupshup da conta), body (a pergunta), options:[textos das",
    "  opções], final (mensagem ao encerrar, opcional), ttl (segundos p/ responder, ex.: 120) }. DEPOIS, para CADA",
    "  opção, use connect com source_handle = o TEXTO EXATO da opção, ligando ao próximo nó (que pode ser OUTRO",
    "  wa_menu = submenu). Use source_handle 'default' para resposta não reconhecida. Menu-após-menu = só ligar nós.",
    "  Não precisa de template (inbound está na janela de 24h). NÃO use branch+context_set para isso — é o wa_menu.",
    "- TEMPLATES de WhatsApp (oficial): para ENVIAR um template que ainda NÃO existe, crie o nó",
    "  conn.gupshup.enviar_template e CHAME propose_whatsapp_template (com o node_id desse nó) para propor o",
    "  template — isso vira uma pendência no canvas que o usuário confirma; o WhatsApp ainda precisa APROVAR",
    "  (assíncrono). Avise o usuário disso em 1 frase. Não invente um templateName de um template que não existe.",
    "- Se o template é um MENU/URA de WhatsApp (opções de atendimento), passe os 'buttons' como QUICK_REPLY —",
    "  UM botão por opção (ex.: Vendas, Suporte, Financeiro). Sem botões, vira só texto e o menu não funciona.",
    "- SEMPRE mande 'examples' com valores REALISTAS para cada variável {{n}} do corpo (a Meta revisa por eles e",
    "  aprova mais rápido). Para ação use botões URL (link de agendamento/site) e/ou PHONE_NUMBER (ligar).",
    "- AO TERMINAR de montar ou editar, CHAME validate_flow e corrija os issues retornados ANTES de dizer que está pronto.",
    "  Não conclua com issues pendentes. Repita validate_flow até ok=true (ou explique ao usuário o que falta).",
    "",
    "VOCÊ PODE PUBLICAR E DISCAR (faça quando o usuário pedir):",
    "- set_name define o nome do fluxo (necessário antes de publicar).",
    "- publish_flow coloca o fluxo no ar. Antes, garanta validate_flow ok e um nome (set_name).",
    "- place_call DISCA agora rodando ESTE fluxo. Para discar/testar uma ligação SAÍDA, o fluxo precisa do",
    "  gatilho 'api_call' (Ao atender chamada) e estar PUBLICADO. Fluxo do tipo 'ligar para alguém e falar X':",
    "  use trigger api_call, monte a fala/lógica, set_name, validate_flow, publish_flow e então place_call(to).",
    "- Se o usuário pedir 'publica e liga', faça a sequência inteira (nome → validar → publicar → discar).",
    "",
    "DIAGNÓSTICO / RESOLVER PROBLEMAS (o que deu errado na execução):",
    "- Você TEM acesso ao histórico de execuções — NUNCA diga que não tem nem peça para o usuário colar logs.",
    "- Quando ele perguntar sobre a última execução, por que falhou, o que deu errado, ou pedir ajuda a resolver um",
    "  problema: chame read_recent_executions para listar as execuções recentes (mais novas primeiro, com status).",
    "  Ache a que interessa (a última, ou a que está com status 'error') e chame de novo com o runId dela para ver a",
    "  cadeia de passos COM os valores brutos: cada passo traz input/output (o body que chegou, parâmetros e saída de",
    "  cada nó) além de message. INSPECIONE esses valores — compare o recebido vs. o esperado (ex.: o code que chegou",
    "  vs. o code salvo) para achar a causa real. EXPLIQUE em linguagem simples o que falhou e, se der, PROPONHA/APLIQUE",
    "  a correção no fluxo (update_node, etc.). Não precisa mais pedir para o usuário colar logs nem logar valores na resposta.",
    "",
    `Tipos de gatilho comuns: ${TRIGGER_TYPES.map(t => t.type).join(", ")}.`,
    "",
    "Catálogo de verbos (JSON) — cada verbo traz params (com required/options/default/label), outputs e flags:",
    JSON.stringify(catalog),
    context || "",
  ];
  // MODO DEMONSTRAÇÃO: usuário leigo explorando — o copiloto NÃO pede informação
  // em tempo real; presume os melhores padrões e decide por ele. Inserido logo
  // após a introdução para ter precedência sobre a regra padrão de perguntar.
  if (opts.demo) {
    lines.splice(4, 0,
      "MODO DEMONSTRAÇÃO (ATIVO) — o usuário é LEIGO e está só EXPLORANDO a plataforma:",
      "- NÃO peça NENHUMA informação em tempo real enquanto monta — NÃO use ask_user durante a construção.",
      "- PRESUMA os melhores padrões e DECIDA por ele, sem perguntar: dê um nome adequado ao agente/fluxo;",
      "  quando precisar de um número, use o NÚMERO DE DEMONSTRAÇÃO da conta; para WhatsApp use SEMPRE a API",
      "  OFICIAL (Gupshup) quando não for especificado; use a voz padrão; e assim por diante.",
      "- Monte o fluxo COMPLETO e funcional sozinho, do início ao fim. Nada de questionário.",
      "- CONECTORES/INTEGRAÇÕES: NÃO abra formulário de configuração (NÃO use create_connector) e NÃO pergunte",
      "  nada sobre isso. Se a integração TEM um NÓ NATIVO (ex.: WhatsApp oficial = conn.gupshup.enviar_whatsapp),",
      "  USE esse nó nativo MESMO com o conector ainda não configurado — deixe o connectorId vazio; NÃO caia em",
      "  http_request. Só use http_request se NÃO existir nó nativo pra aquela integração. No FINAL, mencione em",
      "  1 frase que o conector precisa ser configurado depois pra ativar de verdade.",
      "- NÃO faça perguntas ao usuário em NENHUM momento — nem no fim. Assuma o melhor padrão e SIGA. Se algo",
      "  depender de configuração futura, apenas ANOTE na explicação final (afirmando, sem perguntar).",
      "- AO TERMINAR: garanta validate_flow ok e dê um NOME claro ao fluxo com set_name (salva automaticamente).",
      "  Depois AFIRME em 1–2 frases o que montou e que a pessoa JÁ PODE testar. É PROIBIDO terminar com pergunta",
      "  (nada de 'quer que eu configure?' nem 'prefere testar?') — em demo você DECIDE, não pergunta.",
      "",
    );
  }
  return lines.join("\n");
};
